import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EditBoothModal = ({ isOpen, onClose, booth, onSubmit }) => {
  const [formData, setFormData] = useState({
    instituteName: '',
    description: '',
    website: '',
    contactPerson: '',
    boothType: 'free'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const boothTypes = [
    { 
      value: 'free', 
      label: 'Free Booth',
      description: 'Basic booth with standard features'
    },
    { 
      value: 'premium', 
      label: 'Premium Booth',
      description: 'Enhanced visibility with priority placement'
    },
    { 
      value: 'sponsored', 
      label: 'Sponsored Booth',
      description: 'Top-tier booth with maximum exposure'
    }
  ];

  useEffect(() => {
    if (booth) {
      setFormData({
        instituteName: booth?.instituteName || '',
        description: booth?.description || '',
        website: booth?.website || '',
        contactPerson: booth?.contactPerson || '',
        boothType: booth?.boothType || 'free'
      });
    }
    setError('');
  }, [booth]);

  const handleChange = (e) => {
    if (e?.target) {
      const { name, value } = e?.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    setError('');
  };

  const handleBoothTypeChange = (value) => {
    setFormData(prev => ({ ...prev, boothType: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation
    if (!formData?.instituteName) {
      setError('Institute name is required');
      return;
    }

    setLoading(true);
    try {
      await onSubmit(booth?.id, formData);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to update booth');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !booth) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-heading font-semibold text-foreground">
            Edit Booth
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <Icon name="AlertCircle" size={20} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Read-only info */}
          <div className="bg-muted/30 border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-white">
                <img
                  src={booth?.logoUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_default.png'}
                  alt={`${booth?.instituteName} logo`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="font-semibold text-foreground">{booth?.instituteName}</p>
                <p className="text-sm text-muted-foreground">{booth?.user?.email}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Location:</span>
                <span className="ml-2 text-foreground">{booth?.user?.city || 'N/A'}, {booth?.user?.state || 'N/A'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Courses:</span>
                <span className="ml-2 text-foreground">{booth?.courses?.length || 0} courses</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Institute Name <span className="text-red-500">*</span>
              </label>
              <Input
                name="instituteName"
                value={formData?.instituteName}
                onChange={handleChange}
                placeholder="Enter institute name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Contact Person
              </label>
              <Input
                name="contactPerson"
                value={formData?.contactPerson}
                onChange={handleChange}
                placeholder="Contact person name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Website
              </label>
              <Input
                type="url"
                name="website"
                value={formData?.website}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Booth Type
              </label>
              <Select
                name="boothType"
                value={formData?.boothType}
                onChange={handleBoothTypeChange}
                options={boothTypes}
              />
              <p className="text-xs text-muted-foreground mt-2">
                {formData?.boothType === 'free' && '• Free: Basic booth with standard features and visibility'}
                {formData?.boothType === 'premium' && '• Premium: Enhanced booth with priority placement and additional features'}
                {formData?.boothType === 'sponsored' && '• Sponsored: Top-tier booth with maximum exposure and premium benefits'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData?.description}
                onChange={handleChange}
                placeholder="Brief description about the institute"
                rows={4}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-smooth"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              iconName={loading ? 'Loader2' : 'Save'}
              className="bg-primary text-white hover:bg-primary/90"
            >
              {loading ? 'Saving Changes...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBoothModal;